// TODO: Minor Fixes & Improvements
// * 1. Copyright: Decide final strategy for setting copyright (CMS-driven vs. local config).
// * 2. Phone Formatting: Define standard phone format, create a utility helper, and map 'phone'/'mobile' labels.
// * 3. i18n: Import i18n module and replace static strings (e.g., "Folgen Sie uns", "T", "E") with translation keys.
// * 4. General Review: Audit for performance (memoization) and final responsive design polish.

// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import ContentContainer from '@/components/common/ContentContainer';
import { Icon } from '@/components/common/icons';
import { SITE_FOOTER_DEFAULTS } from '@/components/common/defaults';

// ** Utils
import { getSubdomain } from '@/utils/domain/regions';
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { Footer } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';
import type { SxProps, Theme } from '@mui/material/styles';

export type SiteFooterCopyright = {
	left: string;
	right?: string;
};

// 1. Create a refined type that only includes the specific Strapi fields you need
type FooterContent = Pick<Footer, 'contact' | 'legalLinks' | 'navColumns' | 'socialLinks'>;

// 2. Pass that refined type into StrapiComponentProps
type FooterProps = FooterContent & {
	host: AppContext['host'];
	copyright?: SiteFooterCopyright;
	sx?: SxProps<Theme>;
};

const SeparatorText = () => (
	<Typography
		variant="body-md"
		component="span"
		sx={{
			color: '#FFF',
			textAlign: 'center',
			opacity: 0.4,
			mx: 3,
			'@media (min-width:1120px)': { mx: 14 }
		}}
	>
		|
	</Typography>
);

const LEGAL_LINK_SX = {
	color: '#FFF',
	textAlign: 'center',
	opacity: 0.85,
	transition: 'opacity 0.2s ease, text-decoration-color 0.2s ease',
	textDecoration: 'underline',
	textDecorationColor: 'transparent',
	'&:hover': {
		opacity: 1,
		textDecorationColor: 'rgba(255, 255, 255, 0.6)'
	}
} as const;

const COPYRIGHT_TEXT_SX = {
	color: '#FFF',
	opacity: 0.8,
	whiteSpace: 'pre-line'
} as const;

const CONTACT_LABEL_SX = {
	color: '#FFF',
	opacity: 0.9,
	whiteSpace: 'pre-line'
} as const;

const CONTACT_VALUE_SX = {
	color: '#FFF',
	opacity: 0.85,
	whiteSpace: 'pre-line',
	textDecoration: 'underline',
	textDecorationColor: 'transparent',
	transition: 'opacity 0.2s ease, text-decoration-color 0.2s ease',
	'&:hover': {
		opacity: 1,
		textDecorationColor: 'rgba(255, 255, 255, 0.6)'
	}
} as const;

const CONTACT_LINK_SX = {
	color: '#FFF',
	textDecoration: 'none',
	'&:focusVisible': {
		outline: '2px solid rgba(255, 255, 255, 0.9)',
		outlineOffset: 2,
		borderRadius: 2
	}
} as const;

const SOCIAL_ICON_WRAPPER_SX = {
	width: 40,
	height: 40,
	aspectRatio: '1 / 1',
	display: 'inline-flex',
	opacity: 0.85,
	transition: 'opacity 0.2s ease, transform 0.2s ease',
	'&:hover': {
		opacity: 1,
		transform: 'scale(1.1)'
	}
} as const;

const LEGAL_LINKS_ROW_SX = {
	width: '100%',
	alignItems: 'center',
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'center',
	'@media (min-width:1120px)': {
		justifyContent: 'flex-start'
	}
} as const;

const LEGAL_BUTTON_RESET_SX = {
	background: 'none',
	border: 'none',
	padding: 0,
	margin: 0,
	cursor: 'pointer',
	font: 'inherit',
	color: 'inherit',
	display: 'inline',
	letterSpacing: 'inherit',
	lineHeight: 'inherit'
} as const;

const COOKIE_SETTINGS_HREF = 'https://cookie-settings';

const isCookieSettingsAction = (href: string | null | undefined): boolean =>
	typeof href === 'string' && href === COOKIE_SETTINGS_HREF;

const openCookieSettings = () => {
	try {
		(window as any).CCM?.openWidget();
	} catch {
		// CCM19 not loaded
	}
};

const COPYRIGHT_ROW_SX = {
	display: 'flex',
	gap: 2,
	justifyContent: 'space-between',
	alignItems: 'center',
	flexWrap: 'wrap',
	'@media (min-width:1120px)': { justifyContent: 'flex-end' }
} as const;

const DividerLine = () => (
	<Box
		sx={(theme) => ({
			width: '100%',
			maxWidth: 1280,
			mx: 'auto',
			borderBottom: `1px solid rgba(255, 255, 255, 0.18)`,
			color: theme.palette.common.white
		})}
	/>
);

const interpolatePlaceholders = ({ value, host }: { value: string; host: string }) => {
	const year = String(new Date().getFullYear());
	const land = getSubdomain(host).toUpperCase() || 'NRW';

	return value.replace(/\{year\}/g, year).replace(/\{land\}/g, land);
};

const toPhoneHref = (phone: string) => `tel:${phone.replace(/\s+/g, '')}`;

const SiteFooter = ({
	host,
	navColumns,
	contact,
	socialLinks,
	legalLinks,
	copyright = SITE_FOOTER_DEFAULTS.copyright,
	sx
}: FooterProps) => {
	const overlayBlack = useTheme().palette.text.primary;
	const customIconColor = {
		glyphColor: overlayBlack
	};

	return (
		<Box
			component="footer"
			sx={[
				(theme) => ({
					width: '100%',
					backgroundColor: theme.palette.text.primary,
					color: theme.palette.common.white
				}),
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			<ContentContainer
				disableGutters
				sx={(theme) => ({
					px: theme.layout.pagePx,
					pt: 20,
					pb: {
						xs: `calc(${theme.spacing(theme.layout.desktopColumnGap.xs)} + env(safe-area-inset-bottom, 0px))`,
						sm: `calc(${theme.spacing(theme.layout.desktopColumnGap.sm)} + env(safe-area-inset-bottom, 0px))`,
						lg: `calc(${theme.spacing(theme.layout.desktopColumnGap.lg)} + env(safe-area-inset-bottom, 0px))`
					},
					'@media (min-width:1120px)': { pb: theme.spacing(20) }
				})}
			>
				{/* Top navigation columns */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
						gap: 6
					}}
				>
					{navColumns?.map((col) => {
						if (!col) return null;
						return (
							<Box key={col.title}>
								<Stack spacing={1.5}>
									<Typography variant="label" sx={{ color: '#FFF', fontWeight: 500 }}>
										{col.title}
									</Typography>
									{col.links?.map((link) => {
										if (!link) return null;

										const externalHref = link.hrefExternal;
										const href = buildPathFromRelationLink(link.href);
										const resolvedHref = href !== '/' ? href : externalHref || '/';

										return (
											<Typography key={`${link.label}_${link.id}`} variant="label">
												<Link
													href={resolvedHref}
													passHref
													sx={{
														color: '#FFF',
														opacity: 0.9,
														transition: 'opacity 0.2s ease, text-decoration-color 0.2s ease',
														textDecoration: 'underline',
														textDecorationColor: 'transparent',
														'&:hover': {
															opacity: 1,
															textDecorationColor: 'rgba(255, 255, 255, 0.6)'
														}
													}}
												>
													{link.label}
												</Link>
											</Typography>
										);
									})}
								</Stack>
							</Box>
						);
					})}
				</Box>

				<Box sx={{ mt: 8, mb: 8, '@media (min-width:1120px)': { mt: 10, mb: 10 } }}>
					<DividerLine />
				</Box>

				{/* Middle section: contact + social */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: '1fr',
						gap: 8,
						alignItems: 'start',
						'@media (min-width:1120px)': {
							gridTemplateColumns: 'minmax(0, 1fr) auto'
						}
					}}
				>
					<Box>
						<Stack spacing={0}>
							<Typography variant="label" sx={{ color: '#FFF', fontWeight: 500 }}>
								{contact?.heading}
							</Typography>

							<Box
								sx={{
									mt: 8,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-start',
									flexWrap: 'wrap',
									rowGap: 8,
									'@media (min-width:1120px)': {
										flexDirection: 'row',
										flexWrap: 'nowrap',
										columnGap: 24,
										rowGap: 0
									}
								}}
							>
								<Stack spacing={1}>
									{contact?.addressLines?.map((addressItem) => (
										<Typography
											key={addressItem?.id}
											variant="label"
											sx={{
												color: '#FFF',
												opacity: 0.85,
												whiteSpace: 'pre-line'
											}}
										>
											{addressItem?.content}
										</Typography>
									))}
								</Stack>

								<Stack
									spacing={1}
									sx={{
										flexShrink: 0,
										'@media (min-width:1120px)': { minWidth: 320 }
									}}
								>
									{contact?.phone ? (
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												columnGap: 4
											}}
										>
											<Typography variant="label" sx={CONTACT_LABEL_SX}>
												T
											</Typography>
											<Link
												href={toPhoneHref(contact.phone)}
												passHref
												aria-label={`Telefonnummer anrufen: ${contact.phone}`}
												sx={CONTACT_LINK_SX}
											>
												<Typography variant="label" sx={CONTACT_VALUE_SX}>
													{contact.phone}
												</Typography>
											</Link>
										</Box>
									) : null}

									{contact?.email ? (
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												columnGap: 4
											}}
										>
											<Typography variant="label" sx={CONTACT_LABEL_SX}>
												E
											</Typography>
											<Link
												href={`mailto:${contact.email}`}
												passHref
												aria-label={`E-Mail schreiben an: ${contact.email}`}
												sx={CONTACT_LINK_SX}
											>
												<Typography variant="label" sx={CONTACT_VALUE_SX}>
													{contact.email}
												</Typography>
											</Link>
										</Box>
									) : null}
								</Stack>
							</Box>
						</Stack>
					</Box>

					<Box
						sx={{
							display: 'block',
							'@media (min-width:1120px)': { display: 'none' }
						}}
					>
						<DividerLine />
					</Box>

					<Box>
						<Stack spacing={0}>
							<Typography variant="label" sx={{ color: '#FFF', fontWeight: 500 }}>
								Folgen Sie uns
							</Typography>
							<Stack
								direction="row"
								spacing={2}
								sx={{
									mt: 8,
									alignItems: 'center',
									'@media (min-width:1120px)': { mt: 12 }
								}}
							>
								{socialLinks?.map((social) => {
									if (!social?.key) return null;

									return (
										<Link key={social.key} href={social.href} passHref target="_blank" rel="noopener noreferrer">
											<Box sx={SOCIAL_ICON_WRAPPER_SX}>
												<Icon
													name={social.key.toLowerCase()}
													width={40}
													height={40}
													customIconColor={customIconColor}
												/>
											</Box>
										</Link>
									);
								})}
							</Stack>
						</Stack>
					</Box>
				</Box>

				<Box sx={{ mt: 8, mb: 8, '@media (min-width:1120px)': { mt: 10, mb: 10 } }}>
					<DividerLine />
				</Box>

				{/* Bottom links + copyright */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: '1fr',
						rowGap: 6,
						'@media (min-width:1120px)': {
							gridTemplateColumns: 'minmax(0, 1fr) auto',
							alignItems: 'center',
							columnGap: 6,
							rowGap: 0
						}
					}}
				>
					<Box>
						<Box sx={LEGAL_LINKS_ROW_SX}>
							{legalLinks?.map((item, index) => {
								if (!item) return null;

								const externalHref = item.hrefExternal;
								const href = buildPathFromRelationLink(item.href);
								const isCookieAction = isCookieSettingsAction(externalHref);
								const resolvedHref = href !== '/' ? href : externalHref || '/';

								return (
									<React.Fragment key={item.id}>
										<Box>
											{isCookieAction ? (
												<Box
													component="button"
													aria-label={item.label}
													onClick={openCookieSettings}
													sx={LEGAL_BUTTON_RESET_SX}
												>
													<Typography variant="label" sx={LEGAL_LINK_SX}>
														{item.label}
													</Typography>
												</Box>
											) : (
												<Link href={resolvedHref} passHref>
													<Typography variant="label" sx={LEGAL_LINK_SX}>
														{item.label}
													</Typography>
												</Link>
											)}
										</Box>
										{index < legalLinks.length - 1 && <SeparatorText />}
									</React.Fragment>
								);
							})}
						</Box>
					</Box>

					<Box
						sx={{
							display: 'block',
							'@media (min-width:1120px)': { display: 'none' }
						}}
					>
						<DividerLine />
					</Box>

					<Box>
						<Box sx={COPYRIGHT_ROW_SX}>
							<Typography variant="caption" sx={COPYRIGHT_TEXT_SX}>
								{interpolatePlaceholders({ value: copyright.left ?? '', host })}
							</Typography>
							{copyright.right ? (
								<Typography variant="caption" sx={COPYRIGHT_TEXT_SX}>
									{copyright.right}
								</Typography>
							) : null}
						</Box>
					</Box>
				</Box>
			</ContentContainer>
		</Box>
	);
};

export default SiteFooter;
